/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { TextInput, View, ViewStyle } from "react-native";
import { DatePicker } from "baseui/datepicker";
import { TimePicker } from "baseui/timepicker";
import { TimezonePicker } from "baseui/timezonepicker";
import ButtonBase from "@material-ui/core/ButtonBase";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Fade from "@material-ui/core/Fade";
import Icon from "react-icons-kit";
import { caretDown } from "react-icons-kit/fa/caretDown";

import { Button, LoadingView, Text, styles } from "UpduoLibrary";
import {
  skaleBlue,
  skalePrimaryBlue,
  skaleWhite,
  skaleRed,
  skaleWebBorderGray,
  skaleWebTextGray,
  skaleWebBackgroundGray,
  skaleWebTextBackgroundLightGray,
  colorWithOpacity,
} from "config/UpduoColors";
import { announcementModeList } from "config/Utilities";

import useAnnouncementDetail from "./useAnnouncementDetail";

export default function AnnouncementDetail({
  announcement,
  onRefresh = () => {},
  onDiscardNew = () => {},
  isNew = false,
}) {
  const {
    alertColor,
    alertString,
    clockedTime,
    clockedTimeZone,
    content,
    heading,
    isDeleteConfirmationShown,
    isLoading,
    mode,
    modeMenuEl,
    _handleContentChange,
    _handleDatePickerChange,
    _handleHeadingChange,
    _handleOnDeleteCancel,
    _handlePressDelete,
    _handlePressDiscard,
    _handleSave,
    _handleTimePickerChange,
    _handleTimezonePickerChange,
    _handleClickModeMenu,
    _handleModeMenuClose,
    _handleChangeMode,
  } = useAnnouncementDetail({
    announcement,
    onRefresh,
    onDiscardNew,
    isNew,
  });

  const headingWidth = 100;
  const dropdownItemHeight = 46;

  const menuInputStyles: ViewStyle = {
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: skaleWebBackgroundGray,
    backgroundColor: skaleWebTextBackgroundLightGray,
    height: dropdownItemHeight,
  };

  const _handleGenerateModeMenuItem = (item) => {
    return (
      <MenuItem
        key={item.id}
        value={item}
        style={{
          color: skaleWebTextGray,
          fontFamily: "ObjectSans-Regular",
          width: 350,
          height: dropdownItemHeight,
          marginLeft: 0,
          borderWidth: 1,
        }}
        onClick={() => {
          _handleChangeMode(item);
        }}
      >
        {item.name}
      </MenuItem>
    );
  };

  const _handleGenerateModeOptions = () => {
    return announcementModeList.map((item) =>
      _handleGenerateModeMenuItem(item)
    );
  };

  const saveButton = (
    <Button
      marginLeft={20}
      onPress={() => {
        _handleSave();
      }}
      width={80}
      text={"Save"}
      height={36}
      borderColor={skalePrimaryBlue}
    />
  );

  const userActionShield = (
    <View
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: colorWithOpacity(skaleWhite, 0.7),
        zIndex: 15,
      }}
    >
      <LoadingView topSpacing={100} />
    </View>
  );

  const deleteConfirmation = (
    <View style={{ flexDirection: "row", alignItems: "center", width: 50 }}>
      <Text style={{ color: skaleWebTextGray, marginLeft: 20 }}>
        {"Are you sure you want to delete this announcement?"}
      </Text>
      <Button
        onPress={_handleOnDeleteCancel}
        width={80}
        marginLeft={10}
        text={"Keep"}
        height={28}
        borderWidth={1}
        borderColor={skalePrimaryBlue}
        color={skaleWhite}
        textColor={skalePrimaryBlue}
      />
      <Button
        onPress={_handlePressDelete}
        width={120}
        marginLeft={10}
        text={"Delete"}
        height={28}
        borderWidth={1}
        borderColor={skaleRed}
        color={skaleWhite}
        textColor={skaleRed}
      />
    </View>
  );

  const allModeMenuItems = _handleGenerateModeOptions();

  return (
    <View style={{ flexDirection: "row" }}>
      <View
        style={{
          padding: 20,
          borderWidth: 1,
          borderColor: isNew ? skaleBlue : skaleWebBorderGray,
          backgroundColor: skaleWhite,
          borderRadius: 10,
          marginTop: 30,
          maxWidth: 1000,
          overflow: "hidden",
        }}
      >
        <View style={{ flex: 1, flexDirection: "row", alignItems: "center" }}>
          <View
            style={{
              width: headingWidth,
              marginRight: 20,
            }}
          >
            <Text
              style={{
                color: skaleWebTextGray,
                fontWeight: "bold",
              }}
            >
              {"Delivery Date/Time"}
            </Text>
          </View>
          <View style={{ width: 120 }}>
            <DatePicker
              value={clockedTime}
              onChange={_handleDatePickerChange}
            />
          </View>
          <View style={{ width: 120 }}>
            <TimePicker
              value={clockedTime[0]}
              onChange={_handleTimePickerChange}
              step={1800}
              creatable
            />
          </View>
          <TimezonePicker
            value={clockedTimeZone}
            onChange={_handleTimezonePickerChange}
          />
        </View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginTop: 20,
          }}
        >
          <Text
            style={{
              color: skaleWebTextGray,
              fontWeight: "bold",
              width: headingWidth,
              marginRight: 20,
            }}
          >
            {"Heading Text"}
          </Text>
          <TextInput
            style={[styles.skillInput, { flexGrow: 1 }]}
            value={heading}
            onChangeText={_handleHeadingChange}
          />
        </View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginTop: 10,
          }}
        >
          <Text
            style={{
              color: skaleWebTextGray,
              fontWeight: "bold",
              width: headingWidth,
              marginRight: 20,
            }}
          >
            {"Content Text"}
          </Text>
          <TextInput
            style={[styles.skillInput, { flexGrow: 1 }]}
            value={content}
            onChangeText={_handleContentChange}
          />
        </View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginTop: 10,
          }}
        >
          <Text
            style={{
              color: skaleWebTextGray,
              fontWeight: "bold",
              width: headingWidth,
              marginRight: 20,
            }}
          >
            {"Delivery Mode"}
          </Text>
          <View
            style={[
              styles.dropdown,
              menuInputStyles,
              {
                height: dropdownItemHeight,
                margin: 0,
                alignItems: "flex-start",
                justifyContent: "center",
                overflow: "hidden",
                padding: 0,
              },
            ]}
          >
            <ButtonBase
              aria-controls={"fade-menu"}
              aria-haspopup={"true"}
              onClick={_handleClickModeMenu}
              style={{
                alignItems: "flex-start",
                marginLeft: "2%",
                color: skaleWebTextGray,
                width: 350,
                paddingRight: 20,
              }}
            >
              <Text
                style={{
                  display: "flex",
                  flex: 1,
                  marginTop: 2,
                  alignItems: "flex-start",
                  color: skaleWebTextGray,
                  fontSize: 16,
                }}
              >
                {mode.name}
              </Text>
              <Icon size={20} icon={caretDown} />
            </ButtonBase>
            <Menu
              id={"fade-menu"}
              anchorEl={modeMenuEl}
              open={Boolean(modeMenuEl)}
              onClose={_handleModeMenuClose}
              TransitionComponent={Fade}
              style={{
                marginLeft: -10.5,
                marginTop: 1,
              }}
            >
              {allModeMenuItems}
            </Menu>
          </View>
        </View>
        <View
          style={{ flexDirection: "row", alignItems: "center", marginTop: 20 }}
        >
          <Text
            style={{
              color: alertColor,
              marginRight: 20,
            }}
          >
            {alertString}
          </Text>
          <View style={{ flex: 1 }} />
          {alertString && alertColor === skaleRed ? saveButton : null}
          <Button
            marginLeft={20}
            onPress={_handlePressDiscard}
            width={120}
            text={isNew ? "Discard" : "Delete"}
            height={36}
            borderWidth={1}
            borderColor={skaleRed}
            color={skaleWhite}
            textColor={skaleRed}
          />
        </View>
        {isLoading ? userActionShield : null}
      </View>
      {isDeleteConfirmationShown ? deleteConfirmation : null}
    </View>
  );
}
